import { defineMessages } from 'react-intl';
import { SportTermLookup, SportTerminology } from '../../../lib/types';
import { pluralTerm } from '../../../lib/helpers/term.helper';

export const scope = 'app.components.ImportPlayers';

// {noun} is filled by the pluralize helper so the count word agrees without ICU plural
// syntax — file rows on the err* lines, the sport's athlete term on the snack* summaries.
export const getImportPlayersMessages = (termLookup: SportTermLookup) => {
  const athlete = termLookup[SportTerminology.ATHLETE].toLowerCase();
  const athletes = pluralTerm(athlete);
  const Athlete = termLookup[SportTerminology.ATHLETE];
  const Athletes = pluralTerm(Athlete);
  const session = termLookup[SportTerminology.SESSION].toLowerCase();

  return defineMessages({
    errNameTaken: {
      id: `${scope}.errNameTaken`,
      defaultMessage: `{count} {noun} not imported because another ${athlete} with the same name already exists, with a different date of birth: {names}. Enter the date of birth on file to add them to this assessment, or give one of them a nickname to tell them apart.`,
    },
    errNameTakenUnknownDob: {
      id: `${scope}.errNameTakenUnknownDob`,
      defaultMessage: `{count} {noun} not imported because another ${athlete} with the same name already exists, with no date of birth on file: {names}. Give one of them a nickname to tell them apart.`,
    },
    errInvalid: {
      id: `${scope}.errInvalid`,
      defaultMessage: '{count} {noun} not imported because of missing or invalid details: {names}.',
    },
    errUnknown: {
      id: `${scope}.errUnknown`,
      defaultMessage: "{count} {noun} not imported and we couldn't tell why: {names}. Please try again.",
    },
    errSkipped: {
      id: `${scope}.errSkipped`,
      defaultMessage: '{count} {noun} left out of the import because required details were missing or invalid. Correct them in your file and import again.',
    },
    errImportFailed: {
      id: `${scope}.errImportFailed`,
      defaultMessage: "Couldn't import your file. Please try again.",
    },
    errPartlyFailed: {
      id: `${scope}.errPartlyFailed`,
      defaultMessage: 'Some rows could not be processed and were not imported. Everything listed above did import — compare the list against your file and import the missing rows again.',
    },
    noticeImporting: {
      id: `${scope}.noticeImporting`,
      defaultMessage: 'Importing {count} {noun} — this list updates when the import finishes.',
    },
    lblAllowMissingDob: {
      id: `${scope}.lblAllowMissingDob`,
      defaultMessage: 'Allow missing dates of birth',
    },
    captionAllowMissingDob: {
      id: `${scope}.captionAllowMissingDob`,
      defaultMessage: `Date of Birth stops being a required column, and rows that leave it blank still import. Dates of birth are what tell two same-named ${athletes} apart, so only use this for a file that has none.`,
    },
    lblOverwriteDob: {
      id: `${scope}.lblOverwriteDob`,
      defaultMessage: 'Replace dates of birth already on file',
    },
    captionOverwriteDob: {
      id: `${scope}.captionOverwriteDob`,
      defaultMessage: `Where a name matches an ${athlete} already in your association, the date of birth in your file replaces the one on record — use this to correct placeholder dates with real ones. If you also allow missing dates, a blank row replaces the stored date with the placeholder, overwriting a real date of birth. A name shared by two ${athletes} is left alone for you to tell apart.`,
    },
    captionOverwriteDobUnavailable: {
      id: `${scope}.captionOverwriteDobUnavailable`,
      defaultMessage: 'Unavailable while blank rows take the date of birth already on file — keeping the stored date and replacing it are opposite instructions. Choose the placeholder instead to replace stored dates.',
    },
    noticeKeepDobApplied: {
      id: `${scope}.noticeKeepDobApplied`,
      defaultMessage: 'Dates of birth already on file will be kept. Your file only fills a date where there is none on record.',
    },
    noticeRequiredDobApplied: {
      id: `${scope}.noticeRequiredDobApplied`,
      defaultMessage: 'A row with no date of birth will not be imported, and is listed with the rows that were left out.',
    },
    noticeOverwriteDobApplied: {
      id: `${scope}.noticeOverwriteDobApplied`,
      defaultMessage: 'Dates of birth already on file will be replaced by the ones in your file.',
    },
    noticeOverwriteDobPlaceholder: {
      id: `${scope}.noticeOverwriteDobPlaceholder`,
      defaultMessage: 'Dates of birth already on file will be replaced by the ones in your file. A blank row replaces the stored date with the placeholder, overwriting a real date of birth.',
    },
    noticeMissingDobApplied: {
      id: `${scope}.noticeMissingDobApplied`,
      defaultMessage: 'Missing dates of birth are allowed for this import. When one is missing: {option}.',
    },
    lblMissingDobFallback: {
      id: `${scope}.lblMissingDobFallback`,
      defaultMessage: 'When a date of birth is missing',
    },
    captionMissingDobOnFile: {
      id: `${scope}.captionMissingDobOnFile`,
      defaultMessage: `Each blank row takes the date of birth from the ${athlete} of that name already in your association. A row with no ${athlete} of that name on file gets a placeholder date instead.`,
    },
    captionMissingDobDefault: {
      id: `${scope}.captionMissingDobDefault`,
      defaultMessage: `Every blank row gets a placeholder date, with no lookup. A row whose name already belongs to an ${athlete} with a real date of birth is refused rather than merged, and listed with the rows that were not imported.`,
    },
    snackCreated: {
      id: `${scope}.snackCreated`,
      defaultMessage: `{count} new {noun} added to this assessment.`,
    },
    snackMerged: {
      id: `${scope}.snackMerged`,
      defaultMessage: `{count} {noun} matched to an existing profile and added.`,
    },
    snackSkipped: {
      id: `${scope}.snackSkipped`,
      defaultMessage: '{count} {noun} already on this assessment — nothing changed.',
    },
    snackDuplicateRows: {
      id: `${scope}.snackDuplicateRows`,
      defaultMessage: `{count} duplicate {noun} in your file — each ${athlete} counted once.`,
    },
    snackLeftOut: {
      id: `${scope}.snackLeftOut`,
      defaultMessage: '{count} {noun} left out before the import — correct them in your file and import again.',
    },
    snackNotAdded: {
      id: `${scope}.snackNotAdded`,
      defaultMessage: '{count} {noun} not added — missing or invalid information.',
    },
    snackContactsCreated: {
      id: `${scope}.snackContactsCreated`,
      defaultMessage: '{count} {noun} created.',
    },
    snackContactsMerged: {
      id: `${scope}.snackContactsMerged`,
      defaultMessage: '{count} {noun} merged into existing records.',
    },
    instructionsExisting: {
      id: `${scope}.instructionsExisting`,
      defaultMessage: `${Athletes} already in the system will be added to this assessment with their information updated`,
    },
    // {link} is the template download link, labelled by templateLink.
    templatePrompt: {
      id: `${scope}.templatePrompt`,
      defaultMessage: 'Use your own CSV or start with this template: {link}',
    },
    templateLink: {
      id: `${scope}.templateLink`,
      defaultMessage: `Master ${Athlete} Import Template`,
    },
    lblJerseyRetention: {
      id: `${scope}.lblJerseyRetention`,
      defaultMessage: 'Jerseys and numbers in this file',
    },
    optJerseyRetentionUnchanged: {
      id: `${scope}.optJerseyRetentionUnchanged`,
      defaultMessage: 'Leave the assessment setting as it is ({current})',
    },
    currentJerseyRetentionOn: {
      id: `${scope}.currentJerseyRetentionOn`,
      defaultMessage: `kept from one ${session} to the next`,
    },
    currentJerseyRetentionOff: {
      id: `${scope}.currentJerseyRetentionOff`,
      defaultMessage: `reset each ${session}`,
    },
    optJerseyRetentionRetain: {
      id: `${scope}.optJerseyRetentionRetain`,
      defaultMessage: `Keep them from one ${session} to the next`,
    },
    optJerseyRetentionReset: {
      id: `${scope}.optJerseyRetentionReset`,
      defaultMessage: `Start each ${session} with blank jerseys`,
    },
    captionJerseyRetention: {
      id: `${scope}.captionJerseyRetention`,
      defaultMessage: 'This sets the assessment\'s jersey setting for every age group. You can change it later in the assessment settings.',
    },
    columnOrderNote: {
      id: `${scope}.columnOrderNote`,
      defaultMessage: 'Column order is not enforced. Next steps will match columns to the appropriate field',
    },
  });
};
